import axios from "axios";
import Swal from "sweetalert2";
import authHeader from "./jwt-token-access/auth-token-header";

const API_URL = "https://rscoreapi.azurewebsites.net/api/";

const axiosApi = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
    "Access-Control-Allow-Origin": "*",
  },
});

let inactivityTimeout;

function resetInactivityTimer() {
  if (inactivityTimeout) {
    clearTimeout(inactivityTimeout);
  }
  inactivityTimeout = setTimeout(() => {
    console.warn("User inactive for 5 minutes. Logging out...");
    showSessionExpiredAlert();
  }, 3000000); // 5 minutes
}

export function setupInactivityTimer() {
  console.log("Attaching event listeners for inactivity timer.");
  window.addEventListener("mousemove", resetInactivityTimer);
  window.addEventListener("keydown", resetInactivityTimer);
  window.addEventListener("scroll", resetInactivityTimer);
  window.addEventListener("click", resetInactivityTimer);
  resetInactivityTimer();
}

function showSessionExpiredAlert() {
  Swal.fire({
    title: "Session Expired",
    text: "Your session has expired. Press OK to return to the login page.",
    icon: "warning",
    confirmButtonText: "OK",
    allowOutsideClick: false,
    allowEscapeKey: false,
  }).then(() => {
    localStorage.clear();
    window.location.href = "/login";
  });
}

async function refreshAccessToken() {
  const refreshToken = localStorage.getItem("refreshToken") || "";
  const userName = localStorage.getItem("userName") || "";
  const currentAccessToken = authHeader().Authorization.replace("Bearer ", "");

  if (!refreshToken || !userName) {
    console.warn("No refresh token or username found. Logging out.");
    //showSessionExpiredAlert();
    return;
  }

  try {
    const response = await axios.post(`${API_URL}user/refresh`, {
      userName,
      refreshToken,
      accessToken: currentAccessToken,
    });

    const newToken = response.data.bearerToken;
    const expiresIn = response.data.accessTokenExpiration || 600; // Default to 10 mins

    localStorage.setItem("token", JSON.stringify({ bearerToken: newToken }));
    localStorage.setItem("tokenExpiry", new Date().getTime() + expiresIn * 1000);
    axiosApi.defaults.headers["Authorization"] = `Bearer ${newToken}`;

    console.log("Token refreshed successfully");
    return newToken;
  } catch (error) {
    console.error("Failed to refresh token:", error.response?.data || error.message);
    //showSessionExpiredAlert();
    throw error;
  }
}

axiosApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

      if (originalRequest._retryCount > 3) {
        console.error("Max retry attempts reached. Logging out.");
        //showSessionExpiredAlert();
        return Promise.reject(error);
      }

      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
        return axiosApi(originalRequest);
      } catch (refreshError) {
        console.error("Token refresh failed, logging out.");
        //showSessionExpiredAlert();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

function scheduleTokenRefresh() {
  const expiryTimestamp = localStorage.getItem("tokenExpiry");
  if (!expiryTimestamp) {
    console.warn("No token expiry found, skipping refresh scheduling.");
    return;
  }

   const expiryTime = new Date(parseInt(expiryTimestamp, 10));
   console.log("Token expiry time:", expiryTime.toString());
  const currentTime = new Date().getTime();
  const refreshInMs = Math.max(expiryTime - currentTime - 50000, 0); 
  console.log(`Scheduling token refresh in ${refreshInMs / 1000} seconds`);

  setTimeout(async () => {
    try {
      await refreshAccessToken();
      scheduleTokenRefresh();
    } catch (error) {
      console.error("Automatic token refresh failed:", error);
    }
  }, refreshInMs);
}

// Call this function after user login or token refresh
if (localStorage.getItem("authUser")) {
  //scheduleTokenRefresh();
  setupInactivityTimer();
}

export async function ensureValidToken() {
  console.log("Checking token validity...");
  const tokenExpiry = new Date(localStorage.getItem("tokenExpiry"));
  console.log(tokenExpiry.getTime());
  if (tokenExpiry && new Date().getTime() > tokenExpiry.getTime()) {
    console.warn("Token has expired. Refreshing...");
    await refreshAccessToken();
  }
}

// Update HTTP methods to validate token before making requests
export async function get(url, config = {}) {
  await ensureValidToken();
  return axiosApi
    .get(url, {
      headers: { ...authHeader() },
      ...config,
    })
    .then((response) => response.data);
}

export async function post(url, data, config = {}) {

  return axiosApi
    .post(url, data, {
      headers: { ...authHeader() },
      ...config,
    })
    .then((response) => response.data);
}

export async function put(url, data, config = {}) {
  await ensureValidToken();
  return axiosApi
    .put(url, data, {
      headers: { ...authHeader() },
      ...config,
    })
    .then((response) => response.data);
}

export async function del(url, config = {}) {
  await ensureValidToken();
  return axiosApi
    .delete(url, {
      headers: { ...authHeader() },
      ...config,
    })
    .then((response) => response.data);
}
